import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Brain, Clock, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestPage = ({ user }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(Date.now());
  const [tabSwitchWarnings, setTabSwitchWarnings] = useState(0);
  
  // Refs for callback stability
  const answersRef = useRef(answers);
  const submittingRef = useRef(submitting);
  
  // Keep refs updated
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  
  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  // Submit handler with useCallback
  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    setSubmitting(true);

    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tests/submit`,
        {
          answers: answersRef.current,
          time_taken: timeTaken
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Test submitted successfully!');
      navigate('/result');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit test');
      setSubmitting(false);
    }
  }, [startTime, navigate]);

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/tests/questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuestions(response.data.questions);
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('already attempted')) {
          toast.error('You have already attempted this test');
          navigate('/result');
        } else if (error.response?.status === 403) {
          toast.error('Please complete payment first');
          navigate('/payment');
        } else {
          toast.error('Failed to load questions');
          navigate('/dashboard');
        }
      }
    };

    fetchQuestions();
  }, [navigate]);

  // Timer countdown
  useEffect(() => {
    if (loading || submitting) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, submitting, handleSubmit]);

  // Tab switch detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !submittingRef.current) {
        setTabSwitchWarnings((prev) => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            toast.error('Too many tab switches! Test will be submitted.');
            handleSubmit();
          } else {
            toast.warning(`Warning: Tab switch detected (${newCount}/3)`);
          }
          return newCount;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleSubmit]);

  // Disable right-click and copy-paste
  useEffect(() => {
    const disableCopyPaste = (e) => {
      if (e.type === 'contextmenu' || e.type === 'copy' || e.type === 'cut') {
        e.preventDefault();
        toast.error('Copy/Paste is disabled during the test');
      }
    };

    document.addEventListener('contextmenu', disableCopyPaste);
    document.addEventListener('copy', disableCopyPaste);
    document.addEventListener('cut', disableCopyPaste);

    return () => {
      document.removeEventListener('contextmenu', disableCopyPaste);
      document.removeEventListener('copy', disableCopyPaste);
      document.removeEventListener('cut', disableCopyPaste);
    };
  }, []);

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-indigo-500 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-slate-600">Loading test...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-white/50">
      {/* Sticky Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 shadow-sm z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-indigo-500" />
              <span className="font-heading font-semibold text-slate-900">
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {tabSwitchWarnings > 0 && (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium">{tabSwitchWarnings}/3 warnings</span>
                </div>
              )}
              <div
                data-testid="timer-display"
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  timeLeft < 300 ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'
                }`}
              >
                <Clock className="w-5 h-5" />
                <span className="font-semibold font-mono">{formatTime(timeLeft)}</span>
              </div>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-8">
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                {currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1)}
              </span>
            </div>

            {/* Question */}
            <div className="mb-8">
              <p
                data-testid="question-text"
                className="font-test text-lg text-slate-900 leading-relaxed no-copy"
              >
                {currentQuestion.question_text}
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  data-testid={`option-${index}`}
                  onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all no-copy ${
                    answers[currentQuestion.id] === index
                      ? 'border-indigo-500 bg-indigo-50 shadow-md'
                      : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQuestion.id] === index
                          ? 'border-indigo-500 bg-indigo-500'
                          : 'border-slate-300'
                      }`}
                    >
                      {answers[currentQuestion.id] === index && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                    <span className="font-test text-slate-800">{option}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
              <Button
                data-testid="previous-question-btn"
                onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                className="rounded-full px-6"
              >
                Previous
              </Button>

              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  data-testid="submit-test-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold transition-all hover:scale-105 shadow-lg"
                >
                  {submitting ? 'Submitting...' : 'Submit Test'}
                </Button>
              ) : (
                <Button
                  data-testid="next-question-btn"
                  onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
                  className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-indigo-500/20"
                >
                  Next
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Question Navigator */}
        <Card className="mt-6 bg-white/80 backdrop-blur-md rounded-2xl border border-slate-100">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Question Navigator</h3>
            <div className="grid grid-cols-10 gap-2">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  data-testid={`navigator-btn-${index}`}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    index === currentQuestionIndex
                      ? 'bg-indigo-500 text-white shadow-md'
                      : answers[q.id] !== undefined
                      ? 'bg-teal-100 text-teal-700 border border-teal-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-100 border border-teal-300 rounded"></div>
                <span>Answered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-100 rounded"></div>
                <span>Not Answered</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestPage;
